import React, { useEffect, useRef, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";
import SearchBar from "../components/SearchBar";
import ArticleList from "../components/ArticleList";
import { useSpecialism, specialismCodeMap, specialismCollectionMap, SpecialismCode } from "../contexts/SpecialismContext";
import { Link } from "react-router-dom";

type Article = {
  id: string;
  title: string;
  summary: string;
  category: string;
  subcategory?: string;
  [key: string]: any;
};

const Home: React.FC = () => {
  const { specialismCode, specialism } = useSpecialism();
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [subcategories, setSubcategories] = useState<string[]>([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSticky, setIsSticky] = useState(false);
  const stickyRef = useRef<HTMLDivElement>(null);

  // For smooth article list movement
  const subcatRef = useRef<HTMLDivElement>(null);
  const [subcatHeight, setSubcatHeight] = useState(0);

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      const collectionName = specialismCollectionMap[specialismCode] || "articles";
      const querySnapshot = await getDocs(collection(db, collectionName));
      const articlesData: Article[] = [];
      querySnapshot.forEach((doc) => {
        articlesData.push({ id: doc.id, ...doc.data() } as Article);
      });
      setArticles(articlesData);

      // Extract unique categories
      const uniqueCategories = Array.from(
        new Set(articlesData.map((a) => a.category).filter(Boolean))
      );
      setCategories(uniqueCategories);
      setLoading(false);
    };
    fetchArticles();
  }, [specialismCode]);

  // Only show subcategories that have articles in the selected category
  useEffect(() => {
    const filteredArticles = selectedCategory
      ? articles.filter((a) => a.category === selectedCategory)
      : articles;
    const uniqueSubcategories = Array.from(
      new Set(filteredArticles.map((a) => a.subcategory).filter(Boolean))
    );
    setSubcategories(uniqueSubcategories.filter((s): s is string => typeof s === "string"));
  }, [articles, selectedCategory]);

  // Reset subcategory selection when category changes
  useEffect(() => {
    setSelectedSubcategory(null);
  }, [selectedCategory]);

  // Filter articles by category and subcategory if selected
  const filteredByCategory = selectedCategory
    ? articles.filter((a) => a.category === selectedCategory)
    : articles;

  const filteredBySubcategory = selectedSubcategory
    ? filteredByCategory.filter((a) => a.subcategory === selectedSubcategory)
    : filteredByCategory;

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([e]) => setIsSticky(e.intersectionRatio < 1),
      { threshold: [1] }
    );
    if (stickyRef.current) {
      observer.observe(stickyRef.current);
    }
    return () => {
      if (stickyRef.current) observer.unobserve(stickyRef.current);
    };
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        background: "#f5f6fa",
        fontFamily: "Roboto, Arial, sans-serif",
        position: "relative",
        padding: "0 12px 32px 12px",
      }}
    >

      <h1
        style={{
          fontSize: "2rem",
          fontWeight: "bold",
          margin: "32px 0 16px 0",
          textAlign: "center",
          color: "#222",
          width: "100%",
          maxWidth: 400,
        }}
      >
        Pocketpedia
      </h1>
      {loading ? (
        <div style={{ margin: "32px 0", fontSize: "1.2rem", color: "#888", textAlign: "center" }}>
          Loading...
        </div>
      ) : (
        <>
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: "bold",
              margin: "0px 0 16px 0",
              textAlign: "center",
              color: "#222",
              width: "100%",
              maxWidth: 400,
            }}
          >
            Categorieën
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
              gap: "12px",
              width: "100%",
              maxWidth: 400,
              margin: "0 auto 24px auto",
              justifyItems: "center",
            }}
          >
            <style>
              {`
                @media (max-width: 600px) {
                  .category-btn, .subcategory-btn {
                    height: 56px !important;
                    font-size: 0.8rem !important;
                  }
                }
              `}
            </style>
            <button
              className="category-btn"
              onClick={() => setSelectedCategory(null)}
              style={{
                height: "90px",
                width: "100%",
                maxWidth: "120px",
                background: selectedCategory === null ? "#1976d2" : "#fff",
                color: selectedCategory === null ? "#fff" : "#222",
                border: "none",
                borderRadius: "18px",
                fontWeight: "bold",
                fontSize: "0.85rem",
                boxShadow:
                  selectedCategory === null
                    ? "0 4px 16px rgba(25,118,210,0.15)"
                    : "0 2px 8px rgba(0,0,0,0.08)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                padding: "6px",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "normal",
              }}
            >
              Alles
            </button>
            {categories.map((cat) => (
              <button
                className="category-btn"
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                style={{
                  height: "90px",
                  width: "100%",
                  maxWidth: "120px",
                  background: selectedCategory === cat ? "#1976d2" : "#fff",
                  color: selectedCategory === cat ? "#fff" : "#222",
                  border: "none",
                  borderRadius: "18px",
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                  boxShadow:
                    selectedCategory === cat
                      ? "0 4px 16px rgba(25,118,210,0.15)"
                      : "0 2px 8px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  padding: "6px",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "normal",
                }}
              >
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>

          {/* Always show subcategories */}
          <h2
            style={{
              fontSize: "1.5rem",
              fontWeight: "bold",
              margin: "0px 0 16px 0",
              textAlign: "center",
              color: "#222",
              width: "100%",
              maxWidth: 400,
            }}
          >
            Subcategorieën
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
              gap: "12px",
              width: "100%",
              maxWidth: 400,
              margin: "0 auto 24px auto",
              justifyItems: "center",
            }}
          >
            {subcategories.length >= 2 && (
              <button
                className="subcategory-btn"
                onClick={() => setSelectedSubcategory(null)}
                style={{
                  height: "90px",
                  width: "100%",
                  maxWidth: "120px",
                  background: selectedSubcategory === null ? "#1976d2" : "#fff",
                  color: selectedSubcategory === null ? "#fff" : "#222",
                  border: "none",
                  borderRadius: "18px",
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                  boxShadow:
                    selectedSubcategory === null
                      ? "0 4px 16px rgba(25,118,210,0.15)"
                      : "0 2px 8px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  padding: "6px",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "normal",
                }}
              >
                Alles
              </button>
            )}
            {subcategories.map((subcat) => (
              <button
                className="subcategory-btn"
                key={subcat}
                onClick={() => setSelectedSubcategory(subcat)}
                style={{
                  height: "90px",
                  width: "100%",
                  maxWidth: "120px",
                  background: selectedSubcategory === subcat ? "#1976d2" : "#fff",
                  color: selectedSubcategory === subcat ? "#fff" : "#222",
                  border: "none",
                  borderRadius: "18px",
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                  boxShadow:
                    selectedSubcategory === subcat
                      ? "0 4px 16px rgba(25,118,210,0.15)"
                      : "0 2px 8px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  padding: "6px",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "normal",
                }}
              >
                {subcat.charAt(0).toUpperCase() + subcat.slice(1)}
              </button>
            ))}
          </div>

          {/* Article list */}
          <div
            style={{
              width: "100%",
              maxWidth: 600,
              margin: "0 auto",
              position: "relative",
              zIndex: 1,
            }}
          >
            <ArticleList searchTerm={searchTerm} articles={filteredBySubcategory} />
          </div>
        </>
      )}
    </div>
  );
};

export default Home;